import { useEffect, useRef } from 'react';
import {
  collection,
  getDocs,
  setDoc,
  doc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuth } from '../providers/auth.provider';
import { useSession } from '../providers/session.provider';
import type { Session } from '../providers/session.provider';

type FirestoreHelp = { type: 'hint' | 'answer'; at: Timestamp | null };

type FirestoreSession = {
  value: string;
  started_at: Timestamp | null;
  completed_at: Timestamp | null;
  help: FirestoreHelp[];
};

function deserializeSession(id: string, data: FirestoreSession): Session {
  return {
    id,
    value: data.value ?? '',
    started_at: data.started_at?.toDate() ?? undefined,
    completed_at: data.completed_at?.toDate() ?? undefined,
    help: (data.help ?? []).map((h) => ({
      type: h.type,
      at: h.at?.toDate() ?? undefined,
    })),
  };
}

function serializeSession(session: Session): FirestoreSession {
  return {
    value: session.value,
    started_at: session.started_at ? Timestamp.fromDate(session.started_at) : null,
    completed_at: session.completed_at ? Timestamp.fromDate(session.completed_at) : null,
    help: session.help.map((h) => ({
      type: h.type,
      at: h.at ? Timestamp.fromDate(h.at) : null,
    })),
  };
}

// Firestore document IDs cannot contain '/' — encode session IDs for storage
const encodeSessionId = (id: string) => id.replaceAll('/', '|');
const decodeSessionId = (id: string) => id.replaceAll('|', '/');

export function useProgressSync(onSyncReady: () => void) {
  const { user, loading: authLoading } = useAuth();
  const { sessions, loadSessions } = useSession();
  // Tracks session IDs that came from the last Firestore load — these are
  // skipped on the first write-back pass to avoid an unnecessary round-trip.
  const loadedIdsRef = useRef<Set<string>>(new Set());
  // Snapshot of session entries seen on the previous render, keyed by id.
  // We use entry-level identity (same Session object) to detect changes.
  const prevEntriesRef = useRef<Record<string, Session>>({});
  // Keep a stable ref to the current user so the write-back effect can read it
  // without listing `user` as a dep (which would retrigger on every login).
  const userRef = useRef(user);
  userRef.current = user;

  // On login: load sessions from Firestore
  useEffect(() => {
    if (authLoading) return; // wait for Firebase to resolve before deciding

    if (!user) {
      onSyncReady();
      return;
    }

    const load = async () => {
      try {
        const snapshot = await getDocs(
          collection(db, 'users', user.uid, 'sessions')
        );
        const loaded: Record<string, Session> = {};
        snapshot.docs.forEach((docSnap) => {
          const sessionId = decodeSessionId(docSnap.id);
          loaded[sessionId] = deserializeSession(
            sessionId,
            docSnap.data() as FirestoreSession
          );
        });
        // Record which IDs came from Firestore so write-back skips them
        loadedIdsRef.current = new Set(Object.keys(loaded));
        loadSessions(loaded);
      } catch (err) {
        console.error('[useProgressSync] Failed to load sessions:', err);
      }
      // Mark ready whether load succeeded or failed — editor must not be blocked
      onSyncReady();
    };

    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user?.uid]); // intentionally omit loadSessions/onSyncReady — both are stable per provider contract

  // On every render: diff sessions against the previous snapshot and write
  // changed entries to Firestore. Using a ref-based approach so we detect
  // mutations to the sessions object even when its reference is stable.
  const sessionsRef = useRef(sessions);
  sessionsRef.current = sessions;

  useEffect(() => {
    const currentUser = userRef.current;
    if (!currentUser) return;

    const current = sessionsRef.current;
    const prev = prevEntriesRef.current;
    const writes: Promise<void>[] = [];

    for (const [id, session] of Object.entries(current)) {
      // Skip sessions that were just loaded from Firestore (no need to write back)
      if (loadedIdsRef.current.has(id)) continue;

      // Write if this is a new session or the Session object reference changed
      if (prev[id] !== session) {
        const docRef = doc(db, 'users', currentUser.uid, 'sessions', encodeSessionId(id));
        writes.push(
          setDoc(docRef, serializeSession(session)).catch((err) => {
            console.error(`[useProgressSync] Failed to write session ${id}:`, err);
          })
        );
      }
    }

    // After processing, clear loaded IDs so future changes to those sessions do get written
    loadedIdsRef.current = new Set();
    // Snapshot current entries (shallow copy of the map, keeping the same Session references)
    prevEntriesRef.current = { ...current };

    void Promise.all(writes);
  }); // intentionally no dep array — run on every render to catch object mutations
}
